/**
 * @fileoverview Orchestration Contract
 * @module contracts
 *
 * Workflow orchestration for complex multi-step processes.
 */

import { z } from 'zod';
import {
	createContract,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
	PaginationMetaSchema,
} from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

export enum WorkflowType {
	FULL_TEST_FLOW = 'full_test_flow', // Payment → Test → Analysis → Email → PDF
	QUICK_TEST_FLOW = 'quick_test_flow', // Test → Email → PDF
	ANALYSIS_FLOW = 'analysis_flow', // Analysis → Review → Email
}

export enum WorkflowStatus {
	PENDING = 'pending',
	IN_PROGRESS = 'in_progress',
	PAUSED = 'paused',
	COMPLETED = 'completed',
	FAILED = 'failed',
	CANCELLED = 'cancelled',
}

// ============================================================================
// METADATA TYPES
// ============================================================================

/**
 * Type-safe metadata for workflow execution
 */
export interface WorkflowMetadata {
	testId?: string;
	paymentId?: string;
	customerEmail?: string;
	customerName?: string;
	preAIReviewId?: string;
	postAIReviewId?: string;
	analysisGenerated?: boolean;
	analysisGeneratedAt?: string;
	analysisModel?: string;
	analysisTokensUsed?: number;
	pdfId?: string;
	pdfUrl?: string;
	pdfGeneratedAt?: string;
	emailSent?: boolean;
	emailSentAt?: string;
	emailMessageId?: string;
	pausedReason?: string;
	pausedAt?: string;
	resumeReason?: string;
	retryReason?: string;
	[key: string]: unknown; // Allow for extension while remaining safe
}

export const WorkflowMetadataSchema = z.record(z.string(), z.unknown()).and(z.object({
	testId: z.string().optional(),
	paymentId: z.string().uuid().optional(),
	customerEmail: z.string().email().optional(),
	customerName: z.string().optional(),
	preAIReviewId: z.string().optional(),
	postAIReviewId: z.string().optional(),
	analysisGenerated: z.boolean().optional(),
	analysisGeneratedAt: z.string().optional(),
	analysisModel: z.string().optional(),
	analysisTokensUsed: z.number().optional(),
	pdfId: z.string().optional(),
	pdfUrl: z.string().optional(),
	pdfGeneratedAt: z.string().optional(),
	emailSent: z.boolean().optional(),
	emailSentAt: z.string().optional(),
	emailMessageId: z.string().optional(),
	pausedReason: z.string().optional(),
	pausedAt: z.string().optional(),
	resumeReason: z.string().optional(),
	retryReason: z.string().optional(),
}));

// ============================================================================
// REQUEST SCHEMAS
// ============================================================================

export const StartWorkflowRequestSchema = z.object({
	workflowType: z.nativeEnum(WorkflowType),
	userId: z.string().uuid(),
	testId: z.string().optional(), // testId format: "test_<uuid>" or custom string
	paymentId: z.string().uuid().optional(),
	metadata: WorkflowMetadataSchema.optional(),
});

export const GetWorkflowStatusRequestSchema = z.object({
	workflowId: z.string().uuid(),
});

export const CancelWorkflowRequestSchema = z.object({
	workflowId: z.string().uuid(),
	reason: z.string().optional(),
});

export const RetryWorkflowRequestSchema = z.object({
	workflowId: z.string().uuid(),
	fromStep: z.string().optional(),
	reason: z.string().optional(),
});

export const ResumeWorkflowRequestSchema = z.object({
	workflowId: z.string().uuid(),
	fromStep: z.string().optional(),
	reason: z.string().optional(),
});

export const ListWorkflowsQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	limit: z.coerce.number().int().min(1).max(100).default(20),
	sortBy: z.enum(['startedAt', 'completedAt', 'status']).default('startedAt'),
	sortOrder: z.enum(['asc', 'desc']).default('desc'),
	status: z.nativeEnum(WorkflowStatus).optional(),
	workflowType: z.nativeEnum(WorkflowType).optional(),
	userId: z.string().uuid().optional(),
});

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

export const WorkflowStepSchema = z.object({
	stepName: z.string(),
	status: z.nativeEnum(WorkflowStatus),
	startedAt: z.string().nullable().optional(),
	completedAt: z.string().nullable().optional(),
	error: z.string().nullable().optional(),
});

export const WorkflowExecutionSchema = z.object({
	id: z.string().uuid(),
	workflowType: z.nativeEnum(WorkflowType),
	userId: z.string().uuid(),
	status: z.nativeEnum(WorkflowStatus),
	currentStep: z.string().nullable().optional(),
	steps: z.array(WorkflowStepSchema),
	metadata: WorkflowMetadataSchema.nullable().optional(),
	error: z.string().nullable().optional(),
	startedAt: z.string(),
	completedAt: z.string().nullable().optional(),
});

export const StartWorkflowResponseSchema = z.object({
	success: z.boolean(),
	workflowId: z.uuid().optional(),
	message: z.string().optional(),
});

export const PaginatedWorkflowsSchema = z.object({
	data: z.array(WorkflowExecutionSchema),
	meta: PaginationMetaSchema,
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type StartWorkflowRequest = z.infer<typeof StartWorkflowRequestSchema>;
export type GetWorkflowStatusRequest = z.infer<typeof GetWorkflowStatusRequestSchema>;
export type CancelWorkflowRequest = z.infer<typeof CancelWorkflowRequestSchema>;
export type RetryWorkflowRequest = z.infer<typeof RetryWorkflowRequestSchema>;
export type ResumeWorkflowRequest = z.infer<typeof ResumeWorkflowRequestSchema>;
export type ListWorkflowsQuery = z.infer<typeof ListWorkflowsQuerySchema>;

export type WorkflowStep = z.infer<typeof WorkflowStepSchema>;
export type WorkflowExecution = z.infer<typeof WorkflowExecutionSchema>;
export type StartWorkflowResponse = z.infer<typeof StartWorkflowResponseSchema>;
export type PaginatedWorkflows = z.infer<typeof PaginatedWorkflowsSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export const StartWorkflowApiResponseSchema = ApiSuccessResponseSchema(StartWorkflowResponseSchema);
export const WorkflowExecutionApiResponseSchema = ApiSuccessResponseSchema(WorkflowExecutionSchema);
const CancelWorkflowDataSchema = z.object({ message: z.string() });
export const CancelWorkflowApiResponseSchema = ApiSuccessResponseSchema(CancelWorkflowDataSchema);
const RetryWorkflowDataSchema = z.object({ message: z.string() });
export const RetryWorkflowApiResponseSchema = ApiSuccessResponseSchema(RetryWorkflowDataSchema);
const ResumeWorkflowDataSchema = z.object({ message: z.string() });
export const ResumeWorkflowApiResponseSchema = ApiSuccessResponseSchema(ResumeWorkflowDataSchema);
export const ListWorkflowsApiResponseSchema = ApiPaginatedResponseSchema(WorkflowExecutionSchema);

// ============================================================================
// API CONTRACT
// ============================================================================

export const ORCHESTRATION_API_CONTRACT = createContract('/orchestration', {
	startWorkflow: {
		method: 'POST' as const,
		path: '/start',
		body: StartWorkflowRequestSchema,
		response: StartWorkflowApiResponseSchema,
	},
	getWorkflowStatus: {
		method: 'GET' as const,
		path: '/status/:workflowId',
		params: GetWorkflowStatusRequestSchema,
		response: WorkflowExecutionApiResponseSchema,
	},
	cancelWorkflow: {
		method: 'POST' as const,
		path: '/cancel',
		body: CancelWorkflowRequestSchema,
		response: CancelWorkflowApiResponseSchema,
	},
	retryWorkflow: {
		method: 'POST' as const,
		path: '/:workflowId/retry',
		params: z.object({ workflowId: z.uuid() }),
		body: z.object({
			fromStep: z.string().optional(),
			reason: z.string().optional(),
		}),
		response: RetryWorkflowApiResponseSchema,
	},
	resumeWorkflow: {
		method: 'POST' as const,
		path: '/:workflowId/resume',
		params: z.object({ workflowId: z.uuid() }),
		body: z.object({
			fromStep: z.string().optional(),
			reason: z.string().optional(),
		}),
		response: ResumeWorkflowApiResponseSchema,
	},
	listWorkflows: {
		method: 'GET' as const,
		path: '/workflows',
		query: ListWorkflowsQuerySchema,
		response: ListWorkflowsApiResponseSchema,
	},
});
